// One option card (price, capacity line, stepper, card CTA). Moved verbatim
// from ServicePanel.tsx (zero behaviour change) so AvailabilityPanel renders
// the SAME card instead of a copy. Do not add panel-specific branches here —
// pass data through the existing props.

import { useId, useState } from "react";
import type { WidgetUnit } from "../../types";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../../lib/image";
import { descriptionPreview } from "../../lib/description-preview";
import { Stepper } from "./Stepper";
import { partyRangeLabel } from "../../state/option-led";

export function UnitCard({
  unit,
  payAtVenue = false,
  priceSuffix,
  displayPrice,
  hidePrice = false,
  priceLabel,
  onPriceRetry,
  unavailable,
  unpriced = false,
  selected,
  onSelect,
  onOpenDetail,
  multiSelect = false,
  stepperWhenSelected = false,
  partyMax = null,
  quantity = null,
  onQuantityChange,
  qtyLabel = "qty",
  maxQuantity,
  minQuantity = 1,
  partyNoun,
  cardCta,
  heroPlaceholder = false,
  adjustableWhenUnavailable = false,
  unavailableLabel,
  duration,
  metaPrefix,
  stepperAriaLabel,
  stepperRetainFocus,
  selectLabels,
}: {
  unit: WidgetUnit;
  /** Service-level pay-at-venue: zero/unpriced options settle on site. */
  payAtVenue?: boolean;
  priceSuffix: string;
  /** The window-resolved price (seasonal rules / Cloudbeds rate calendar).
   *  The card's price line is the FIRST number the guest sees, and it was the
   *  one surface still reading the flat synced base_price — an October search
   *  showed the September probe rate while the hold charged the calendar one.
   *  The charge and the first impression must be the same number. */
  displayPrice?: number | null;
  /** The venue has prices switched off: show no money at all. Distinct from a
   *  null displayPrice, which falls back to the option's own base price — that
   *  fallback is what kept showing the number a venue had turned off. "Pay at
   *  venue" and the unavailable/unpriced feet still speak. */
  hidePrice?: boolean;
  priceLabel?: string;
  onPriceRetry?: () => void;
  unavailable: boolean;
  /** An unavailable card the guest can still ADJUST. Unavailability that
   *  depends on the card's own count (a headcount the stock cannot seat) is
   *  only escaped from the card, so the greyed card stays reachable: a
   *  single-select card can still be picked, a ticked multi-select card can
   *  be unticked, and a selected card keeps its stepper so the count can come
   *  down. The foot Add stays disabled — nothing is held while greyed.
   *  Default false: an unavailable card is inert. */
  adjustableWhenUnavailable?: boolean;
  /** Price-foot text while unavailable (default "Not available for these
   *  dates") — lets the parent say WHY, e.g. the headcount that did not fit. */
  unavailableLabel?: string;
  duration?: {
    minutes: number;
    choices: number[];
    disabled: boolean;
    onChange: (minutes: number) => void;
    /** How a length reads (default "{minutes} minutes") — e.g. a tenant's "Half day". */
    format?: (minutes: number) => string;
  };
  /** Text rendered before the meta line's "Up to N · " (e.g. the option's own length). */
  metaPrefix?: string;
  /** Accessible name for the card's stepper (default "Quantity"). */
  stepperAriaLabel?: string;
  /** Keep keyboard focus on "+" at the ceiling (aria-disabled, not disabled).
   *  Defaults to adjustableWhenUnavailable, which also turns it on. */
  stepperRetainFocus?: boolean;
  /** The pick button's words when the card carries no cart CTA (default
   *  Book / Selected ✓, or Add / Remove for multi-select) — a panel that
   *  books later, from one summary button, says "Choose". */
  selectLabels?: { idle: string; selected: string };
  // Accommodation unit with NO rate loaded (base_price null). Reads "Rates
  // unavailable" instead of the old "Included" — which implied the room was
  // free — and Add is disabled: the server rejects such holds anyway
  // (trg_bookings_guard_unpriced_accommodation), this just says why first.
  unpriced?: boolean;
  selected: boolean;
  onSelect: () => void;
  onOpenDetail?: (unit: WidgetUnit) => void;
  // #351: multi-select mode turns the radio dot into a checkbox and, once
  // ticked, shows a per-option quantity stepper (guests / item count).
  multiSelect?: boolean;
  // Option-led single-select: the headcount lives ON the picked option, next
  // to its "Up to N" and its per-person price — so the guest is never asked
  // "how many?" before they can see what they are choosing. Shows the same
  // stepper multi-select shows on a ticked card, but bound to the party.
  stepperWhenSelected?: boolean;
  // Option-led single: the party ceiling when it exceeds one unit's capacity
  // (two 4-seat bays serve 8) — shown as "Up to 8 · 4 per unit".
  partyMax?: number | null;
  quantity?: number | null;
  onQuantityChange?: (n: number) => void;
  qtyLabel?: string;
  // Upper bound on the stepper (accommodation passes inventory_count).
  maxQuantity?: number;
  /** Lower bound on the stepper: a per-person option's fewest guests per
   *  booking (units[].party_size_min). The meta line then reads "2–4 riders"
   *  instead of "Up to 4". Default 1: no floor, the card reads as before. */
  minQuantity?: number;
  /** The headcount noun for that range ("riders"); default guest/guests. */
  partyNoun?: { one: string; other: string };
  /** Render a tinted name panel in place of a missing photo. Set by the parent
   *  only when SOME sibling has a photo — a wholly text-only list stays
   *  text-only rather than growing decorative blocks it never had. */
  heroPlaceholder?: boolean;
  // Everything-on-the-card flow: quantity + Add/Remove act directly on the
  // cart from the card, so the guest never hunts for a separate action
  // button. When the unit already has cart lines (inCartCount > 0) the card
  // shows an "In booking × N ✓" badge and the CTA flips to Remove. Absent
  // (events keep the pick-then-CTA flow) → legacy behaviour throughout.
  cardCta?: {
    inCartCount: number;
    busy: boolean;
    checking?: boolean;
    checkingLabel?: string;
    addDisabled?: boolean;
    // No booking window picked yet — the Add button reads "Pick a date first"
    // (muted, still clickable: onAdd nudges the setup row) and the stepper
    // stays hidden until dates exist.
    needsDate: boolean;
    // Option-led: the card button IS the booking action, so it says so.
    addLabel?: string;
    needsDateLabel?: string;
    onAdd: () => void;
    onRemove: () => void;
    // Opens the cart drawer — the "Checkout →" button on in-cart cards.
    onCheckout?: () => void;
  };
}) {
  // The description reads as body text under the facts line: a short preview
  // (its first sentence, or its first ~100 characters cut at a word with "…";
  // description-preview.ts), and "Read more" for the whole text — shown in
  // place, never a modal.
  const [descOpen, setDescOpen] = useState(false);
  const descId = useId();
  const desc = descriptionPreview(unit.description);
  const effective = hidePrice ? null : (displayPrice ?? unit.base_price);
  const price =
    effective != null
      ? `${unit.currency ?? "ZAR"} ${Number(effective).toLocaleString()}`
      : null;
  const gallery = (unit.gallery_image_urls ?? []).filter(Boolean);
  const amenities = (unit.amenities ?? []).filter(Boolean);
  // A room card opens a detail modal (photos + amenities) on click; a plain
  // unit with no media keeps the original click-to-select behaviour.
  const hasDetail =
    Boolean(unit.image_url) || gallery.length > 0 || amenities.length > 0;
  const canOpen = hasDetail && Boolean(onOpenDetail);
  // A hero block renders for a real photo, or for the placeholder that keeps a
  // mixed grid even. Both drop the card's outer padding.
  const showEmptyHero = !unit.image_url && heroPlaceholder;
  const hasHero = Boolean(unit.image_url) || showEmptyHero;

  // adjustableWhenUnavailable: a greyed card that can still be picked (single)
  // or unticked (multi). An unticked multi-select card stays inert — ticking
  // a greyed option would only add a line the hold refuses.
  const adjustable =
    unavailable &&
    adjustableWhenUnavailable &&
    !unpriced &&
    (!multiSelect || selected);

  const priceEl = unavailable ? (
    <div className="vt-option-foot vt-option-foot-unavail">
      {unavailableLabel ?? "Not available for these dates"}
    </div>
  ) : priceLabel ? (
    <div className="vt-option-foot" role="status" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      {priceLabel}{onPriceRetry && <> · <button type="button" onClick={onPriceRetry}>Retry price</button></>}
    </div>
  ) : payAtVenue && (unpriced || effective == null || Number(effective) === 0) ? (
    // Pay-at-venue service: a zero/unset price means "settle on site" —
    // "Included" (or a bare R0) told guests it was free, which it isn't.
    <div className="vt-option-foot vt-option-foot-free">Pay at venue</div>
  ) : unpriced ? (
    <div className="vt-option-foot vt-option-foot-unavail">
      Rates unavailable — please enquire
    </div>
  ) : price !== null ? (
    <div className="vt-option-foot">
      {price}
      {priceSuffix}
    </div>
  ) : hidePrice ? (
    // No foot at all: "Included" would answer a question the venue chose not
    // to answer, and would be a lie on a priced option.
    null
  ) : (
    <div className="vt-option-foot vt-option-foot-free">Included</div>
  );

  // Length, then how many: "5 h · 2–4 riders". The description is not part
  // of it any more (it has its own paragraph below).
  const partyFacts =
    minQuantity >= 2
      ? // A floor: say both ends ("2–4 riders"), or the floor alone.
        `${partyRangeLabel(
          minQuantity,
          partyMax ?? unit.capacity ?? null,
          partyNoun ?? { one: "guest", other: "guests" },
        )}${partyMax && unit.capacity && partyMax > unit.capacity ? ` · ${unit.capacity} per unit` : ""}`
      : partyMax && unit.capacity && partyMax > unit.capacity
        ? `Up to ${partyMax} · ${unit.capacity} per unit`
        : partyMax && unit.capacity && partyMax < unit.capacity
          ? // A party ceiling below one unit's size (a venue's "block"
            // limit) is the real ceiling the stepper stops at.
            `Up to ${partyMax}`
          : unit.capacity
            ? `Up to ${unit.capacity}`
            : "";
  const facts = [metaPrefix, partyFacts].filter(Boolean).join(" · ");

  const select = () => {
    if ((!unavailable || adjustable) && !unpriced) onSelect();
  };
  const openDetail = () => onOpenDetail?.(unit);
  // Clicking the card opens the detail modal when there's something to show,
  // otherwise it falls back to selecting the unit (non-accommodation units).
  // In multi-select the card IS the checkbox — clicking it ticks the option
  // rather than opening the photo/detail modal (photos stay on the "View
  // details" button). Without this, an option with an image forces a modal
  // between every pick, defeating the "tick several at once, then Add" flow.
  const primaryClick = canOpen && !multiSelect ? openDetail : select;

  const inCart = (cardCta?.inCartCount ?? 0) > 0;
  const needsDate = Boolean(cardCta?.needsDate);
  // Remove never holds anything, so an adjustableWhenUnavailable card's
  // Remove stays live even once its own hold has used up the stock it reads.
  const removeLive = unavailable && adjustableWhenUnavailable && inCart;
  // ...including on a card that is otherwise inert (a multi-select option
  // unticks once added): it gets the same parts-greyed look as an adjustable
  // card, so its live Remove and Checkout read as usable, but no pointer.
  const liveRemoveOnly = removeLive && !adjustable;

  // Per-card quantity stepper. With cardCta every AVAILABLE option shows it
  // (default 1) as the count the foot Add will hold — hidden while the unit
  // is already in the cart (Remove first, then re-Add at the new count;
  // in-cart quantities are never mutated directly) and until a booking
  // window exists (qty is meaningless before a date). Without cardCta the
  // legacy #351 gating stays: ticked multi-select options only. Clicks
  // inside it must not bubble to the card (which would toggle the tick off).
  // Stepper floors at 1, so the guest unticks by clicking the card, not by
  // stepping to 0. An adjustable greyed card keeps it while selected — that
  // stepper is the guest's way back to a count that fits.
  const showStepper = cardCta
    ? (!unavailable || (adjustable && selected)) &&
      !unpriced &&
      !inCart &&
      !needsDate &&
      // Option-led single: the party stepper rides on the PICKED session only.
      (!stepperWhenSelected || selected)
    : (multiSelect || stepperWhenSelected) && selected;
  const qtyStepper =
    showStepper && onQuantityChange ? (
      // stopPropagation on BOTH click and keydown — otherwise Enter/Space on a
      // stepper button bubbles to the card's onKeyDown and untoggles the option.
      // With cardCta the stepper lives in the foot row (compact variant) so it
      // aligns across cards; legacy multi-select keeps the mid-card placement.
      <div
        className={`vt-option-qty${cardCta ? " vt-option-qty-foot" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Stepper
          value={Math.max(minQuantity, quantity && quantity > 0 ? quantity : 1)}
          onChange={onQuantityChange}
          min={minQuantity}
          max={maxQuantity}
          retainFocusAtLimit={stepperRetainFocus ?? adjustableWhenUnavailable}
          ariaLabel={stepperAriaLabel}
        />
        <span className="vt-option-qty-label">{qtyLabel}</span>
      </div>
    ) : null;

  // "In booking × N ✓" — the unit's holds are secured in the cart; the foot
  // CTA reads Remove while this shows.
  const inCartBadge =
    cardCta && inCart ? (
      <div className="vt-option-incart" role="status">
        In booking × {cardCta.inCartCount} ✓
      </div>
    ) : null;

  // Foot CTA. With cardCta the button acts on the CART directly (Add ⇄
  // Remove, busy only on the initiating card); before a window exists it
  // reads "Pick a date first" (muted, still clickable — onAdd only nudges
  // the setup row). The legacy variant keeps the select/Book toggle for the
  // pick-then-CTA flow.
  const footCta = cardCta ? (
    <button
      type="button"
      className={`vt-option-card-book${
        inCart
          ? " vt-option-card-book-remove"
          : needsDate
            ? " vt-option-card-book-needsdate"
            : ""
      }`}
      onClick={(e) => {
        e.stopPropagation();
        if (cardCta.busy || (!inCart && (cardCta.checking || cardCta.addDisabled))) return;
        if (inCart) cardCta.onRemove();
        else cardCta.onAdd();
      }}
      disabled={
        (unavailable && !removeLive) ||
        (unpriced && !inCart) ||
        cardCta.busy || (!inCart && (!!cardCta.checking || !!cardCta.addDisabled))
      }
    >
      {cardCta.busy
        ? "Holding…"
        : inCart
          ? "Remove"
          : needsDate
            ? (cardCta.needsDateLabel ?? "Pick a date first")
            : cardCta.checking ? (cardCta.checkingLabel ?? "Checking…") : (cardCta.addLabel ?? "Add")}
    </button>
  ) : (
    <button
      type="button"
      className={`vt-option-card-book${selected ? " vt-option-card-book-on" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        select();
      }}
      disabled={unavailable || unpriced}
    >
      {/* Multi-select ticking only SELECTS the item — it isn't in the
          booking until the "Add N to booking" CTA is pressed. So the
          ticked state reads "Remove" (a clear toggle-off), never
          "Added", which falsely implied it was already in the cart. */}
      {selectLabels
        ? selected
          ? selectLabels.selected
          : selectLabels.idle
        : selected
          ? multiSelect
            ? "Remove"
            : "Selected ✓"
          : multiSelect
            ? "Add"
            : "Book"}
    </button>
  );

  // In-cart cards offer the next step right where the guest is: a primary
  // "Checkout →" beside the quiet Remove, opening the cart drawer.
  const checkoutBtn =
    cardCta && inCart && cardCta.onCheckout ? (
      <button
        type="button"
        className="vt-option-card-book vt-option-card-checkout"
        onClick={(e) => {
          e.stopPropagation();
          cardCta.onCheckout?.();
        }}
      >
        Checkout →
      </button>
    ) : null;

  return (
    <div
      className={`vt-option-card${selected ? " vt-option-selected" : ""}${hasHero ? " vt-option-card-has-image" : ""}${unavailable ? " vt-option-unavailable" : ""}${adjustable ? " vt-option-adjustable" : ""}${liveRemoveOnly ? " vt-option-live-remove" : ""}${duration ? " vt-option-with-duration" : ""}`}
      role="button"
      tabIndex={unavailable && !adjustable && !canOpen ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={unavailable && !adjustable && !canOpen}
      onClick={primaryClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          primaryClick();
        }
      }}
    >
      {/* Hero strip with the room name overlaid — consistent with the event
          and functions-venue cards. Falls back to the original text-only head
          when no image is configured, so older setups render unchanged. */}
      {unit.image_url && (
        <div className="vt-option-card-hero">
          <img
            className="vt-cover-img"
            style={unit.image_position ? { objectPosition: unit.image_position } : undefined}
            src={optimizedImageUrl(unit.image_url, { width: 800 })}
            srcSet={optimizedSrcSet(unit.image_url, IMG_WIDTHS.CARD)}
            sizes="(min-width: 900px) 25vw, (min-width: 600px) 50vw, 100vw"
            alt=""
            loading="lazy"
            decoding="async"
          />
          {multiSelect && (
            <span
              className={`vt-option-hero-check${selected ? " vt-option-hero-check-on" : ""}`}
              aria-hidden="true"
            >
              {selected ? "✓" : ""}
            </span>
          )}
          <div className="vt-option-card-hero-name">{unit.name}</div>
          {canOpen && (
            <button
              type="button"
              className="vt-option-card-more"
              onClick={(e) => {
                e.stopPropagation();
                openDetail();
              }}
            >
              View details
            </button>
          )}
        </div>
      )}
      {showEmptyHero && (
        <div className="vt-option-card-hero vt-option-card-hero-empty">
          {multiSelect && (
            <span
              className={`vt-option-hero-check${selected ? " vt-option-hero-check-on" : ""}`}
              aria-hidden="true"
            >
              {selected ? "\u2713" : ""}
            </span>
          )}
          <div className="vt-option-card-hero-name">{unit.name}</div>
          {canOpen && (
            <button
              type="button"
              className="vt-option-card-more"
              onClick={(e) => {
                e.stopPropagation();
                openDetail();
              }}
            >
              View details
            </button>
          )}
        </div>
      )}
      <div className="vt-option-card-body">
        {!hasHero && (
          <div className="vt-option-head">
            <span
              className={multiSelect ? "vt-option-check" : "vt-radio"}
              aria-hidden="true"
            />
            <div className="vt-option-name">{unit.name}</div>
          </div>
        )}
        {/* The facts (length, how many) on their own line, above the
            description rather than run into it. */}
        {facts && <div className="vt-option-meta">{facts}</div>}
        {desc && (
          <p className="vt-option-desc">
            <span id={descId}>{descOpen || !desc.more ? desc.full : desc.lead}</span>
            {desc.more ? (
              <>
                {" "}
                <button
                  type="button"
                  className="vt-option-desc-more"
                  aria-expanded={descOpen}
                  aria-controls={descId}
                  // The card itself is clickable (tick / pick / details):
                  // reading more must never do any of that.
                  onClick={(event) => {
                    event.stopPropagation();
                    setDescOpen((open) => !open);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {descOpen ? "Show less" : "Read more"}
                  <span className="vt-option-desc-sr"> about {unit.name}</span>
                </button>
              </>
            ) : null}
          </p>
        )}
        {duration && (
          <div className="vt-option-duration" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            {duration.choices.length > 1 ? (
              <label>Duration <select aria-label={`${unit.name} duration`} value={duration.minutes}
                disabled={duration.disabled || inCart} onChange={(event) => duration.onChange(Number(event.target.value))}>
                {duration.choices.map((minutes) => <option key={minutes} value={minutes}>{duration.format ? duration.format(minutes) : <>{minutes} minutes</>}</option>)}
              </select></label>
            ) : <span>{duration.format ? duration.format(duration.minutes) : <>{duration.minutes} minutes</>}</span>}
          </div>
        )}
        {inCartBadge}
        {/* Legacy multi-select keeps its mid-card stepper; with cardCta the
            stepper moved into the foot row below so it aligns across cards. */}
        {!cardCta && qtyStepper}
        {/* Foot row (price + CTA) renders for TEXT-ONLY cards too whenever the
            card carries its own cart CTA — previously bare priceEl, which left
            those cards with no action and sent the guest hunting for the
            bottom button. Events (no cardCta) keep the media-only foot row.
            With cardCta the right side groups stepper + Add (or Remove +
            Checkout once in cart); margin-top:auto on the row pins it to the
            card bottom so it sits level across a grid row. */}
        {hasHero || cardCta ? (
          <div className="vt-option-card-foot-row">
            {priceEl}
            {cardCta ? (
              <div
                className="vt-option-card-foot-actions"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {qtyStepper}
                {footCta}
                {checkoutBtn}
              </div>
            ) : (
              footCta
            )}
          </div>
        ) : (
          priceEl
        )}
      </div>
    </div>
  );
}

export type UnitCardProps = Parameters<typeof UnitCard>[0];
